import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { ShiftRemark } from "../models/ShiftRemark";
import { StackerBundle, StackerBundleDraft } from "../models/StackerBundle";
import { MILL_1 } from "@/setup/mill-config/mill-config";

const moment = getMomentTz();

export interface StackerReport {
	bundles: StackerBundle[];
	remark: ShiftRemark | null;
}

export const getStackerReport = async (
	params: { shiftId: number; defaultAreaId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
	];

	const resp = await apiService.callV2(
		params.defaultAreaId == MILL_1.id
			? "[M1].[GetStackerReportByShift]"
			: "[M2].[GetStackerReportByShift]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching stacker report");
	}

	let bundles = get(resp, "data.tables[0].rows", []) as StackerBundle[];
	bundles = bundles.map((b) => ({
		...b,
		CreatedAt: b.CreatedAt ? moment.utc(b.CreatedAt).toDate() : null,
		ModifiedAt: b.ModifiedAt ? moment.utc(b.ModifiedAt).toDate() : null,
	}));

	const remarkRow = get(
		resp,
		"data.tables[1].rows[0]",
		null,
	) as ShiftRemark | null;

	const remark = remarkRow
		? {
				...remarkRow,
				CreatedAt: remarkRow.CreatedAt
					? moment.utc(remarkRow.CreatedAt).toDate()
					: null,
				ModifiedAt: remarkRow.ModifiedAt
					? moment.utc(remarkRow.ModifiedAt).toDate()
					: null,
			}
		: null;

	return { bundles, remark };
};

export const upsertStackerBundle = async (params: {
	draft: StackerBundleDraft;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();
	const { draft } = params;
	const isM1 = params.defaultAreaId == MILL_1.id;

	const parameters: spParameter[] = isM1
		? [
				{ name: "StackerBundleId", value: draft.StackerBundleId },
				{ name: "ShiftId", value: draft.ShiftId },
				{ name: "SectionId", value: draft.SectionId },
				{ name: "HeatId", value: draft.HeatId },
				{ name: "LengthInches", value: draft.LengthInches },
				{ name: "GoodPieces", value: draft.GoodPieces ?? 0 },
				{ name: "TotalWeight", value: draft.TotalWeight },
				{ name: "ScrapPieces", value: draft.ScrapPieces ?? null },
				{ name: "ScrapReason", value: draft.ScrapReason },
			]
		: [
				{ name: "StackerBundleId", value: draft.StackerBundleId },
				{ name: "ShiftId", value: draft.ShiftId },
				{ name: "SectionId", value: draft.SectionId },
				{ name: "HeatId", value: draft.HeatId },
				{ name: "LengthInches", value: draft.LengthInches },
				{ name: "GoodBundles", value: draft.GoodBundles },
				{ name: "GoodPiecesPerBundle", value: draft.GoodPiecesPerBundle },
				{
					name: "TotalPieces",
					value: draft.GoodBundles * draft.GoodPiecesPerBundle,
				},
				{ name: "TotalWeight", value: draft.TotalWeight },
				{ name: "ScrapBundles", value: draft.ScrapBundles },
				{ name: "ScrapPiecesPerBundle", value: draft.ScrapPiecesPerBundle },
				{ name: "ScrapReason", value: draft.ScrapReason },
			];

	const resp = await apiService.callV2(
		isM1 ? "[M1].[UpsertStackerBundle]" : "[M2].[UpsertStackerBundle]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving bundle");
	}
};

export const deleteStackerBundle = async (params: {
	stackerBundleId: number;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "StackerBundleId", value: params.stackerBundleId },
	];

	const resp = await apiService.callV2(
		params.defaultAreaId == MILL_1.id
			? "[M1].[DeleteStackerBundle]"
			: "[M2].[DeleteStackerBundle]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting bundle");
	}
};

export const upsertStackerShiftRemark = async (params: {
	shiftId: number;
	comment: string | null;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
		{ name: "Comment", value: params.comment },
	];

	const resp = await apiService.callV2(
		params.defaultAreaId == MILL_1.id
			? "[M1].[UpsertStackerShiftRemark]"
			: "[M2].[UpsertStackerShiftRemark]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving remark");
	}
};
